import type { Me, Settings } from '../api/types'
import { FLAECHE_DUNKEL, fuerDunkel, gedaempftAuf, lesbarAuf, schriftAuf } from './kontrast'

/**
 * Die Farben der Reiseübersicht (#54) — und die Kette, an deren Ende sie gelten.
 *
 * Drei Flächen: die Zeile einer **eigenen** Reise, die Zeile einer **fremden**,
 * und die **leere** Fläche darunter. Zwei Zeilenfarben und nicht eine, weil die
 * eigene Reise sich abheben soll (#53) — den Streifen an ihrer linken Kante
 * trägt das Stylesheet zusätzlich, denn eine Markierung, die allein an einer
 * wählbaren Farbe hängt, kann man versehentlich abschalten.
 *
 * **Und seit 1.59.0 eine Marke: die Zahl der ungelesenen Beiträge.** Aus dem
 * Betrieb kam sie als vierte Farbe desselben Abschnitts, und sie ist deshalb
 * ein Eintrag hier und keine zweite Mechanik daneben.
 *
 * **`art` ist der Unterschied, und er ist keine Feinheit.** Eine Fläche liegt
 * unter Text und wird im dunklen Modus **abgedunkelt** (`fuerDunkel()`); eine
 * Marke soll auffallen und wird **aufgehellt**, bis sie gegen den dunklen
 * Grund lesbar ist (`lesbarAuf()`, wie der Akzent an den Knöpfen). Das sagt
 * schon die heutige Vorgabe: `--unread` wechselt in `tokens.css` von
 * `#007aff` auf das hellere `#0a84ff`. Durch `fuerDunkel()` geschickt würde
 * ein gewähltes Blau abends zu einem dunklen Marineton auf dunklem Grund —
 * die Zahl wäre da und niemand sähe sie.
 */
export const LISTENFARBEN = [
  {
    key: 'own',
    einstellung: 'list_own_color',
    variable: '--liste-eigen',
    /**
     * **Die Schriftfarbe gehört zur Fläche und nicht zur Seite** (#73).
     *
     * Hier stand nur die Fläche, und die Schrift kam über `color: inherit`
     * aus `--text`. Im dunklen Modus springt `--text` auf Weiß, während die
     * eingestellte Fläche hell bleibt — gemessen an einem Bildschirmfoto vom
     * iPad: `#d7e3fe` mit weißer Schrift, **1,29:1**. Die Sprechblasen hatten
     * ihre Schrift längst gerechnet; die Zeilen nicht. Eine Regel an zwei
     * Stellen, von denen eine fehlte.
     */
    schrift: '--liste-eigen-text',
    leise: '--liste-eigen-leise',
    art: 'flaeche',
    vorgabe: '#e8f0fe',
  },
  {
    key: 'other',
    einstellung: 'list_other_color',
    variable: '--liste-fremd',
    schrift: '--liste-fremd-text',
    leise: '--liste-fremd-leise',
    art: 'flaeche',
    vorgabe: '#ffffff',
  },
  {
    key: 'bg',
    einstellung: 'list_bg_color',
    variable: '--liste-grund',
    schrift: '--liste-grund-text',
    leise: '--liste-grund-leise',
    art: 'flaeche',
    vorgabe: '#f2f2f7',
  },
  {
    key: 'unread',
    einstellung: 'list_unread_color',
    variable: '--liste-ungelesen',
    schrift: '--liste-ungelesen-text',
    /**
     * **Eine Zahl hat nichts Gedämpftes.** Die drei Flächen tragen darin
     * Uhrzeit und Vorschautext; hier steht nur die Zahl selbst, und ein
     * Merkmal ohne Gebrauch wäre eines zu viel.
     */
    leise: null,
    art: 'marke',
    /** Was ohne Wahl gilt: das Blau aus `tokens.css`. */
    vorgabe: '#007aff',
  },
] as const

/** Der Name einer Listenfarbe, wie ihn die Oberfläche führt. */
export type Listenfarbe = (typeof LISTENFARBEN)[number]['key']

/** Fläche unter Text, oder Marke, die auffallen soll — siehe Tabellenkopf. */
export type Listenart = (typeof LISTENFARBEN)[number]['art']

/** Ein vollständiger Satz — leer heißt „nichts gesetzt". */
export type Listensatz = Record<Listenfarbe, string>

/**
 * Ein Satz aus der Tabelle bauen — **die eine Stelle, die die Schlüssel zählt.**
 *
 * Hier standen vier Aufzählungen (`LEER`, `listenKette`, `ausKonto`,
 * `ausEinstellungen`), jede mit denselben drei Namen, zwei Zeilen unter der
 * Tabelle, die sie schon aufzählt. Mit dem vierten Feld (1.59.0) wären es
 * sechzehn Namen gewesen, und eine vergessene Zeile hieße: die Farbe lässt
 * sich wählen, kommt aber nie an. Also einmal hier.
 */
function satz(hol: (eintrag: (typeof LISTENFARBEN)[number]) => string): Listensatz {
  return Object.fromEntries(LISTENFARBEN.map((e) => [e.key, hol(e)])) as Listensatz
}

export const LEER: Listensatz = satz(() => '')

/**
 * **Die Kette: das eigene Konto schlägt die Voreinstellung** (#54).
 *
 * Zwei Stufen, und `''` heißt jeweils „eine Stufe höher". Ganz oben heißt es
 * „wie das Erscheinungsbild" — dann wird die Variable **weggenommen** und nicht
 * gesetzt; nur so behält die dunkle Darstellung ihren eigenen Ton.
 *
 * **Vererbt und nicht kopiert**, anders als die Blasenfarbe einer Reise (#49):
 * dort soll ein laufender Bericht sein Aussehen behalten, wenn jemand anderswo
 * etwas dreht. Hier ist das Gegenteil gewünscht und steht so im Issue — „mit
 * einem Knopf auf die Voreinstellung zurück" heißt, dass sie weiter gilt,
 * solange nichts Eigenes dasteht.
 *
 * Als reine Funktion, damit ein Lauf sie messen kann.
 */
export function listenKette(
  konto: Partial<Listensatz> | null | undefined,
  vorein: Partial<Listensatz> | null | undefined,
): Listensatz {
  return satz((e) => konto?.[e.key] || vorein?.[e.key] || '')
}

/** Was der Server am Konto liefert, auf Zeichenketten gebracht. */
export function ausKonto(me: Me | undefined): Listensatz {
  return satz((e) => me?.list_colors?.[e.key] ?? '')
}

/** Dasselbe aus den allgemeinen Einstellungen. */
export function ausEinstellungen(werte: Settings | undefined): Listensatz {
  return satz((e) => werte?.[e.einstellung] ?? '')
}

/**
 * Ein Satz so, wie ihn der Server entgegennimmt.
 *
 * Das Gegenstück zu `ausKonto()` und `ausEinstellungen()`, und aus demselben
 * Grund hier: die beiden Karten (eigene Farben und Vorgabe der Verwaltung)
 * bauten dieses Objekt je selbst. Zwei Aufzählungen derselben Schlüssel sind
 * genau der Zwilling, von dem einer stehen bleibt.
 */
export function fuerServer(werte: Listensatz): Record<string, string> {
  return Object.fromEntries(LISTENFARBEN.map((e) => [e.einstellung, werte[e.key]]))
}

/**
 * Die Variablen für einen Satz Farben — **drei je Rolle statt einer** (#73).
 *
 * Was leer bleibt, kommt **nicht** vor: der Aufrufer nimmt eine fehlende
 * Variable wieder weg, statt sie auf einen Wert zu setzen — dieselbe Regel wie
 * bei den Sprechblasen. Nur so gilt wieder, was das Erscheinungsbild vorgibt,
 * und zwar hell wie dunkel.
 *
 * **`dunkel` rechnet die Farbe um, statt sie wegzunehmen.** Eingestellt wird
 * im hellen Erscheinungsbild; abends steht dieselbe helle Fläche in einer
 * schwarzen Liste. `fuerDunkel()` dreht die Helligkeit und behält den Ton —
 * das Vorbild ist die Browser-Erweiterung *Noir*, die aus dem eingestellten
 * `#d7e3fe` ein `#1e2426` macht. Wer sich Blau eingestellt hat, sieht abends
 * ein dunkles Blau und nicht ein anderes Grau.
 */
export function listenVariablen(
  werte: Listensatz,
  dunkel = false,
): Record<string, string | null> {
  const raus: Record<string, string | null> = {}
  for (const eintrag of LISTENFARBEN) {
    const stil = listenStil(werte[eintrag.key], dunkel, eintrag.art)
    raus[eintrag.variable] = stil?.background ?? null
    raus[eintrag.schrift] = stil?.color ?? null
    if (eintrag.leise !== null) {
      raus[eintrag.leise] = stil === undefined ? null : gedaempftAuf(stil.background)
    }
  }
  return raus
}

/**
 * Eine Zeile so, wie sie am Ende aussieht — **für die Vorschau** (#73).
 *
 * **Und ausdrücklich dieselbe Rechnung wie oben.** Die Vorschau in den
 * Einstellungen setzte nur die Fläche; die Schrift kam von der Seite, und
 * umgerechnet wurde gar nichts. Im dunklen Erscheinungsbild stand dort
 * deshalb weiterhin die helle Farbe mit weißer Schrift — gemessen **1,29:1**,
 * dieselbe Zahl wie am ursprünglichen Befund. Und sie zeigte etwas anderes,
 * als die Liste daneben aussah.
 *
 * **Eine Vorschau, die anders aussieht als das Ergebnis, ist keine.** Der Satz
 * stand schon im Stylesheet an den Maßen dieser Zeilen; für die Farben galt er
 * nicht. Deshalb geht beides jetzt durch diese eine Stelle.
 *
 * `undefined` heißt „nichts gewählt": der Aufrufer setzt dann gar keinen Stil
 * und es gilt, was das Erscheinungsbild vorgibt — dieselbe Regel wie bei den
 * Variablen.
 */
export function listenStil(
  farbe: string,
  dunkel: boolean,
  art: Listenart = 'flaeche',
): { background: string; color: string } | undefined {
  if (farbe === '') return undefined
  /*
   * **Hell gilt die Wahl, wie sie getroffen wurde** — eingestellt wird im
   * hellen Erscheinungsbild. Nur abends gehen die beiden Arten auseinander:
   * eine Fläche wird abgedunkelt, eine Marke aufgehellt. Der Grund steht am
   * Kopf der Tabelle.
   */
  const gilt = !dunkel ? farbe : art === 'marke' ? lesbarAuf(farbe, FLAECHE_DUNKEL) : fuerDunkel(farbe)
  return { background: gilt, color: schriftAuf(gilt) }
}
